import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styles from '../../styles/components/modules/safety-module.module.css';

interface SafetyButton {
  id: string;
  label: string;
  type: 'green' | 'white';
  badge?: number;
  warningCode?: string; // 对应的预警代码
}

interface SafetyModuleProps {
  onButtonClick: (buttonId: string) => void;
  onWarningClick: () => void;
  warningCount?: Record<string, number>;
}

const SafetyModule: React.FC<SafetyModuleProps> = ({
  onButtonClick,
  onWarningClick,
  warningCount
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 处理预警标准按钮点击
  const handleWarningClick = () => {
    const params = new URLSearchParams();
    const deptId = searchParams.get('deptId');
    const identity = searchParams.get('identity');
    const deptName = searchParams.get('deptName');
    
    if (deptId) params.set('deptId', deptId);
    if (identity) params.set('identity', identity);
    if (deptName) params.set('deptName', deptName);
    params.set('module', 'safety');
    
    navigate(`/warning-standard?${params.toString()}`);
  };
  // 食品安全模块按钮配置，根据预警接口文档映射预警代码
  const getButtons = (): SafetyButton[] => {
    const baseButtons: Omit<SafetyButton, 'badge' | 'type'>[] = [
      { id: 'supplier-manage', label: '进货查验', warningCode: 'CODE0030001' },
      { id: 'supplier-manage1', label: '食品留样', warningCode: 'CODE0030002' },
      { id: 'supplier-manage2', label: '食品添加', warningCode: 'CODE0030003' },
      { id: 'supplier-manage3', label: '餐具消毒', warningCode: 'CODE0030004' },
      { id: 'quality-check', label: '餐碗消毒', warningCode: 'CODE0030005' },
      { id: 'safety-monitor', label: '刀砧消毒', warningCode: 'CODE0030006' },
      { id: 'trace-system', label: '肉机消毒', warningCode: 'CODE0030007' },
      { id: 'hygiene-check', label: '明厨亮灶', warningCode: 'CODE0030013' },
      { id: 'emergency-handle', label: '领导陪餐', warningCode: 'CODE0030010' },
      { id: 'risk-assess', label: '健康上岗', warningCode: 'CODE0030014' },
      { id: 'incident-report', label: '厨废净化', warningCode: 'CODE0030009' },
      { id: 'incident-report1', label: '食品检测', warningCode: 'CODE0030015' },
      { id: 'incident-report2', label: '病媒防制', warningCode: 'CODE0030008' },
      { id: 'incident-report3', label: '安全投诉', warningCode: 'CODE0030011' },
      { id: 'hygiene-check1', label: '信息公开', warningCode: 'CODE0030012' }
    ];

    return baseButtons.map(button => {
      const alertCount = warningCount?.[button.warningCode!] || 0;
      return {
        ...button,
        type: alertCount > 0 ? 'white' : 'green', // 动态设置type
        badge: alertCount > 0 ? alertCount : undefined
      };
    });
  };

  const buttons = getButtons();

  return (
    <div className={styles.safetyModule}>
      {/* 标题区域 - 使用bg-title.png */}
      <div className={styles.moduleHeader}>
        <h2 className={styles.moduleTitle}>食品安全</h2>
      </div>

      {/* 内容区域 - 使用bg-content.png */}
      <div className={styles.contentContainer}>
        {/* 预警标准按钮 */}
        <div className={styles.warningSection}>
          <button
            className={styles.warningButton}
            onClick={handleWarningClick}
          >
            预警标准 &gt;
          </button>
        </div>

        {/* 按钮区域 */}
        <div className={styles.buttonsContainer}>
          {/* 按钮按行排列，每行3个 */}
          {Array.from({ length: Math.ceil(buttons.length / 3) }, (_, rowIndex) => (
            <div key={rowIndex} className={styles.buttonRow}>
              {buttons.slice(rowIndex * 3, (rowIndex + 1) * 3).map((button) => (
                <button
                  key={button.id}
                  className={`${styles.functionButton} ${styles[button.type]}`}
                  onClick={() => {
                    const params = new URLSearchParams();
                    const deptId = searchParams.get('deptId');
                    const identity = searchParams.get('identity');
                    const deptName = searchParams.get('deptName');
                    
                    if (deptId) params.set('deptId', deptId);
                    if (identity) params.set('identity', identity);
                    if (deptName) params.set('deptName', deptName);
                    if (button.warningCode) params.set('warningCode', button.warningCode);
                    
                    navigate(`/warning-detail?${params.toString()}`);
                  }}
                >
                  {button.label}
                  {button.badge && (
                    <span className={styles.badge}>{button.badge}</span>
                  )}
                </button>
              ))}
              {/* 如果最后一行不足3个按钮，用空白占位 */}
              {buttons.slice(rowIndex * 3, (rowIndex + 1) * 3).length < 3 &&
                Array.from({ length: 3 - buttons.slice(rowIndex * 3, (rowIndex + 1) * 3).length }, (_, emptyIndex) => (
                  <div key={`empty-${emptyIndex}`} className={styles.emptySlot}></div>
                ))
              }
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SafetyModule;
